"use client";

import { c as _c } from "react/compiler-runtime";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { IconXmarkOutline18 } from "nucleo-ui-outline-18";
import * as React from "react";
import { cn } from "@/lib/utils";
function findScrollableElement(target: EventTarget | null, fallback: HTMLElement): HTMLElement {
  let element = target instanceof HTMLElement ? target : fallback;
  while (element && element !== fallback) {
    const style = window.getComputedStyle(element);
    const overflowY = style.overflowY;
    const canScroll = (overflowY === "auto" || overflowY === "scroll") && element.scrollHeight > element.clientHeight;
    if (canScroll) {
      return element;
    }
    if (!element.parentElement) break;
    element = element.parentElement;
  }
  return fallback;
}
function preventScrollBounce(event: React.WheelEvent<HTMLElement>) {
  const container = event.currentTarget;
  const scrollable = findScrollableElement(event.target, container);
  const {
    scrollTop,
    scrollHeight,
    clientHeight
  } = scrollable;
  const deltaY = event.deltaY;
  const isAtTop = scrollTop <= 0;
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
  if (deltaY < 0 && isAtTop || deltaY > 0 && isAtBottom) {
    event.preventDefault();
  }
}
function preventTouchBounce(event: React.TouchEvent<HTMLElement>, startY: number | null) {
  if (startY === null || event.touches.length !== 1) return;
  const container = event.currentTarget;
  const scrollable = findScrollableElement(event.target, container);
  const currentY = event.touches[0]?.clientY;
  if (currentY === undefined) return;
  const deltaY = currentY - startY;
  const {
    scrollTop,
    scrollHeight,
    clientHeight
  } = scrollable;
  const isAtTop = scrollTop <= 0;
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
  if (deltaY > 0 && isAtTop || deltaY < 0 && isAtBottom) {
    event.preventDefault();
  }
}
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ease-out-quart fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm duration-200 data-[state=closed]:pointer-events-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <DialogPrimitive.Overlay ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  hideCloseButton?: boolean;
  preventCloseOnOutsideClick?: boolean;
  /** Overrides the inner content card (padding, overflow, bg). The outer glass frame is always present. */
  innerClassName?: string;
  /** Extra classes for the backdrop overlay (e.g. higher z-index for stacked modals). */
  overlayClassName?: string;
}>((t0, ref) => {
  const $ = _c(39);
  let children;
  let className;
  let innerClassName;
  let overlayClassName;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      innerClassName,
      overlayClassName,
      children,
      hideCloseButton: t1,
      preventCloseOnOutsideClick: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = innerClassName;
    $[4] = overlayClassName;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    children = $[1];
    className = $[2];
    innerClassName = $[3];
    overlayClassName = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const hideCloseButton = t1 === undefined ? false : t1;
  const preventCloseOnOutsideClick = t2 === undefined ? false : t2;
  const touchStartYRef = React.useRef(null);
  const t3 = preventCloseOnOutsideClick && "pointer-events-none bg-black/60";
  let t4;
  if ($[8] !== overlayClassName || $[9] !== t3) {
    t4 = cn(t3, overlayClassName);
    $[8] = overlayClassName;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <DialogOverlay className={t4} />;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== className) {
    t6 = cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-[0.97] data-[state=open]:zoom-in-[0.97] data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] ease-out-quart fixed left-[50%] top-[50%] w-full max-w-lg origin-center translate-x-[-50%] translate-y-[-50%] duration-200 data-[state=closed]:pointer-events-none", "rounded-ds-22 shadow-ds-overlay border-ds-border bg-ds-alpha-white-15 dark:bg-ds-alpha-white-5 border p-2 backdrop-blur-md", "z-[10000]", className);
    $[13] = className;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  let t8;
  if ($[15] !== preventCloseOnOutsideClick) {
    t7 = e => {
      if (preventCloseOnOutsideClick) {
        e.preventDefault();
        return;
      }
      const target = e.target as HTMLElement;
      if (target?.closest?.("[data-radix-popper-content-wrapper]")) {
        e.preventDefault();
      }
    };
    t8 = e_0 => {
      if (preventCloseOnOutsideClick) {
        e_0.preventDefault();
        return;
      }
      const target_0 = e_0.target as HTMLElement;
      if (target_0?.closest?.("[data-radix-popper-content-wrapper]")) {
        e_0.preventDefault();
      }
    };
    $[15] = preventCloseOnOutsideClick;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  let t9;
  if ($[18] !== innerClassName) {
    t9 = cn("bg-ds-surface-card rounded-ds-lg shadow-ds-b3-card relative grid max-h-[calc(85vh-12px)] gap-4 overflow-y-auto overscroll-none p-6", innerClassName);
    $[18] = innerClassName;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  let t11;
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = event => {
      touchStartYRef.current = event.touches[0]?.clientY ?? null;
    };
    t11 = event_0 => {
      preventTouchBounce(event_0, touchStartYRef.current);
    };
    t12 = () => {
      touchStartYRef.current = null;
    };
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t10 = $[20];
    t11 = $[21];
    t12 = $[22];
  }
  let t13;
  if ($[23] !== hideCloseButton) {
    t13 = !hideCloseButton && <DialogPrimitive.Close className="focus:outline-hidden text-ds-text-secondary focus:bg-ds-surface-muted data-[state=open]:bg-ds-surface-muted data-[state=open]:text-ds-text-secondary absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:opacity-100 focus:ring-0 disabled:pointer-events-none"><IconXmarkOutline18 className="h-4 w-4" /><span className="sr-only">Close</span></DialogPrimitive.Close>;
    $[23] = hideCloseButton;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== children || $[26] !== t13 || $[27] !== t9) {
    t14 = <div className={t9} onWheelCapture={preventScrollBounce} onTouchStart={t10} onTouchMove={t11} onTouchEnd={t12}>{children}{t13}</div>;
    $[25] = children;
    $[26] = t13;
    $[27] = t9;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== props || $[30] !== ref || $[31] !== t14 || $[32] !== t6 || $[33] !== t7 || $[34] !== t8) {
    t15 = <DialogPrimitive.Content ref={ref} className={t6} onPointerDownOutside={t7} onInteractOutside={t8} {...props}>{t14}</DialogPrimitive.Content>;
    $[29] = props;
    $[30] = ref;
    $[31] = t14;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15 || $[37] !== t5) {
    t16 = <DialogPortal>{t5}{t15}</DialogPortal>;
    $[36] = t15;
    $[37] = t5;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return t16;
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col space-y-1.5 text-center sm:text-left", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
DialogHeader.displayName = "DialogHeader";
const DialogFooter = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
DialogFooter.displayName = "DialogFooter";
const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-lg font-semibold leading-none tracking-tight", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <DialogPrimitive.Title ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("body-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <DialogPrimitive.Description ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;
export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };